import { useAuthStore, GROWTHPAL_ACCESS_TOKEN, requestSsoAccessToken, SSO_DASHBOARD_URL } from "../store/authStore";

const API_BASE = "/api";

let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) =>
    error ? reject(error) : resolve(token)
  );
  refreshQueue = [];
};


class ApiClient {
  async request(endpoint, options = {}, isRetry = false) {
    const state = useAuthStore.getState();

    if (!state.isAuthReady) {
      await state.initAuth();
    }

    let backendToken = useAuthStore.getState().token;
    if (!backendToken && state.loginToBackend) {
      const relogged = await state.loginToBackend();
      if (relogged) {
        backendToken = useAuthStore.getState().token;
      }
    }
    if (!backendToken) {
      throw new Error("Unable to authenticate with dashboard backend");
    }

    const gpToken = useAuthStore.getState().getGrowthPalToken?.()
      ?? localStorage.getItem(GROWTHPAL_ACCESS_TOKEN);

    const headers = {
      "Content-Type": "application/json",
      ...options.headers,
      ...(backendToken && { Authorization: `Bearer ${backendToken}` }),
      ...(gpToken && { "X-GrowthPal-Token": gpToken }),
    };

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    if (response.ok) {
      return response.json();
    }

    if (response.status === 401 && isRetry) {
      useAuthStore.getState().logout();
      throw new Error("Session expired. Please log in again.");
    }

    if (response.status === 401 && !isRetry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({
            resolve: () => resolve(this.request(endpoint, options, true)),
            reject,
          });
        });
      }

      isRefreshing = true;

      try {
        const newGpToken = await requestSsoAccessToken();

        localStorage.setItem(GROWTHPAL_ACCESS_TOKEN, newGpToken);
        useAuthStore.getState().setGrowthPalToken(newGpToken);

        await useAuthStore.getState().loginToBackend();

        processQueue(null, newGpToken);

        return this.request(endpoint, options, true);
      } catch (err) {
        processQueue(err);
        useAuthStore.getState().logout();
        window.location.href = `${SSO_DASHBOARD_URL}?redirectTo=${encodeURIComponent(window.location.href)}`;
        throw new Error("Session expired. Please log in again.");
      } finally {
        isRefreshing = false;
      }
    }

    const error = await response.json().catch(() => ({ message: "Request failed" }));
    throw new Error(error.message || "Request failed");
  }

  get(endpoint) {
    return this.request(endpoint, { method: "GET" });
  }

  post(endpoint, data) {
    return this.request(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  put(endpoint, data) {
    return this.request(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  patch(endpoint, data) {
    return this.request(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  delete(endpoint) {
    return this.request(endpoint, { method: "DELETE" });
  }
}

export const api = new ApiClient();

export const authApi = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  me: () => api.get("/auth/me"),
};

export const targetsApi = {
  list: () => api.get("/targets"),
  get: (id) => api.get(`/targets/${id}`),
  getValuation: (id) => api.get(`/targets/${id}/valuation`),
  saveValuation: (id, data) => api.post(`/targets/${id}/valuation`, data),
  resetValuation: (id) => api.post(`/targets/${id}/valuation/reset`),
  updateStatus: (id, status) => api.patch(`/targets/${id}/status`, { status }),
  getDefaults: () => api.get("/targets/valuation/defaults"),

  fetchProfile: (profileId) => {
    const gpToken = useAuthStore.getState().getGrowthPalToken();
    return api.request(`/targets/profile/${profileId}`, {
      method: "GET",
      headers: { "X-GrowthPal-Token": gpToken },
    });
  },

  importProfile: (profileId) => {
    const gpToken = useAuthStore.getState().getGrowthPalToken();
    return api.request(`/targets/import/${profileId}`, {
      method: "POST",
      headers: { "X-GrowthPal-Token": gpToken },
    });
  },
};

export const matchingApi = {
  findDeals: (data) => api.post("/match/deals", data),
  getSectorMedians: () => api.get("/sectors/medians"),
  getSectorMedian: (sector) => api.get(`/sectors/medians/${encodeURIComponent(sector)}`),
};

export const roiApi = {
  calculate: (data) => api.post("/roi/calculate", data),
};

export const mandatesApi = {
  list: () => {
    const gpToken = useAuthStore.getState().getGrowthPalToken();
    return api.request("/mandates", {
      method: "GET",
      headers: { "X-GrowthPal-Token": gpToken },
    });
  },

  getShortlist: (mandateId) => {
    const gpToken = useAuthStore.getState().getGrowthPalToken();
    return api.request(`/mandates/${mandateId}/shortlist`, {
      method: "GET",
      headers: { "X-GrowthPal-Token": gpToken },
    });
  },

  getCompanyDetails: (mandateId, profileId) => {
    const gpToken = useAuthStore.getState().getGrowthPalToken();
    return api.request(`/mandates/${mandateId}/shortlist/${profileId}/details`, {
      method: "GET",
      headers: { "X-GrowthPal-Token": gpToken },
    });
  },
};